import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Plus,
  Search,
  Camera,
  UserCheck,
  Map,
  Package,
  BarChart3,
  ChevronRight,
  Euro,
  TrendingUp,
  Calendar,
  Building2,
  PenTool,
  Wallet,
  ChevronUp,
  ChevronDown,
  Edit3,
  RotateCcw,
  User,
  Tag
} from 'lucide-react';

// Sub-pages
import DashboardHome from './backoffice/DashboardHome';
import ClientsList from './backoffice/ClientsList';
import ClientDetails from './backoffice/ClientDetails';
import Dashboard from './backoffice/Dashboard';
import CalendarView from './backoffice/CalendarView';
import DevisList from './backoffice/DevisList';
import DevisEditor from './backoffice/DevisEditor';
import PrestationsList from './backoffice/PrestationsList';
import CategoriesManagement from './backoffice/CategoriesManagement';
import PrestatairesList from './backoffice/PrestatairesList';
import RoadmapsList from './backoffice/RoadmapsList';
import RoadmapEditor from './backoffice/RoadmapEditor';
import RoadmapCreator from './backoffice/RoadmapCreator';
import ContratsList from './backoffice/ContratsList';
import RolesList from './backoffice/RolesList';
import PrestataireUsers from './backoffice/PrestataireUsers';
import SiteManager from './backoffice/SiteManager';
import EvenementsList from './backoffice/EvenementsList';
import EvenementDetail from './backoffice/EvenementDetail';
import CompanySettings from './backoffice/CompanySettings';
import UserProfile from './backoffice/UserProfile';
import NotesAuteurList from './backoffice/NotesAuteurList';
import NoteAuteurEditor from './backoffice/NoteAuteurEditor';

const BackOffice = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const defaultMenuItems = [
    { path: '/backoffice', icon: LayoutDashboard, label: 'Tableau de bord', exact: true },
    { path: '/backoffice/calendar', icon: Calendar, label: 'Calendrier' },
    { path: '/backoffice/clients', icon: Users, label: 'Clients' },
    { path: '/backoffice/devis', icon: FileText, label: 'Devis' },
    { path: '/backoffice/contrats', icon: PenTool, label: 'Contrats' },
    { path: '/backoffice/notes-auteur', icon: FileText, label: 'Notes d\'Auteur' },
    { path: '/backoffice/finances', icon: Wallet, label: 'Finances' },
    { path: '/backoffice/roadmaps', icon: Map, label: 'Feuilles de route' },
    { path: '/backoffice/prestations', icon: Package, label: 'Prestations' },
    { path: '/backoffice/categories', icon: Tag, label: 'Catégories' },
    { path: '/backoffice/prestataires', icon: UserCheck, label: 'Prestataires' },
    { path: '/backoffice/roles', icon: Settings, label: 'Rôles/Postes' },
    { path: '/backoffice/company', icon: Building2, label: 'Paramètres Entreprise' },
    { path: '/backoffice/profile', icon: User, label: 'Mon Profil' },
    { path: '/backoffice/site', icon: Camera, label: 'Gestion du Site' },
  ];

  // Load menu order from localStorage or use default
  const [menuItems, setMenuItems] = useState(() => {
    const saved = localStorage.getItem('menuOrder');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultMenuItems;
      }
    }
    return defaultMenuItems;
  });

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const moveMenuItem = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === menuItems.length - 1)
    ) {
      return;
    }

    const newItems = [...menuItems];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]];
    
    setMenuItems(newItems);
    localStorage.setItem('menuOrder', JSON.stringify(newItems));
  };

  const resetMenuOrder = () => {
    setMenuItems(defaultMenuItems);
    localStorage.removeItem('menuOrder');
    toast.success('Ordre du menu réinitialisé');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-stone-100 flex relative">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${sidebarOpen ? 'w-64' : 'lg:w-20 w-64'}
        fixed lg:relative inset-y-0 left-0 z-50
        bg-stone-900 text-white transition-all duration-300 flex flex-col
      `}>
        {/* Logo */}
        <div className="p-4 border-b border-stone-700 flex items-center justify-between">
          {sidebarOpen && (
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo-kolnoa.svg" alt="Kolnoa" className="h-8 w-auto" />
              <span className="text-xs text-stone-400 font-semibold">ERP</span>
            </Link>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
            data-testid="toggle-sidebar"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Edit Mode Toggle */}
        {sidebarOpen && (
          <div className="px-4 pt-4 pb-2 border-b border-stone-700">
            <button
              onClick={() => setEditMode(!editMode)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                editMode ? 'bg-amber-600 text-white' : 'bg-stone-800 text-stone-300 hover:bg-stone-700'
              }`}
            >
              <Edit3 className="h-4 w-4" />
              {editMode ? 'Terminer' : 'Réorganiser menu'}
            </button>
            {editMode && (
              <button
                onClick={resetMenuOrder}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm mt-2 bg-stone-800 text-stone-300 hover:bg-stone-700"
              >
                <RotateCcw className="h-4 w-4" />
                Réinitialiser
              </button>
            )}
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item, index) => (
            <div key={item.path} className="flex items-center gap-1">
              <Link
                to={item.path}
                className={`flex-1 flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-amber-600 text-white'
                    : 'text-stone-300 hover:bg-stone-800 hover:text-white'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                {sidebarOpen && <span className="text-sm">{item.label}</span>}
              </Link>
              
              {/* Edit Mode Controls */}
              {editMode && sidebarOpen && (
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveMenuItem(index, 'up')}
                    disabled={index === 0}
                    className={`p-1 rounded ${
                      index === 0
                        ? 'text-stone-600 cursor-not-allowed'
                        : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                    }`}
                  >
                    <ChevronUp className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => moveMenuItem(index, 'down')}
                    disabled={index === menuItems.length - 1}
                    className={`p-1 rounded ${
                      index === menuItems.length - 1
                        ? 'text-stone-600 cursor-not-allowed'
                        : 'text-stone-400 hover:bg-stone-800 hover:text-white'
                    }`}
                  >
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User */}
        <div className="p-4 border-t border-stone-700">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 bg-amber-600 rounded-full flex items-center justify-center">
                {user?.name?.[0] || 'U'}
              </div>
            )}
            {sidebarOpen && (
              <div className="overflow-hidden">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-stone-400 truncate">{user?.email}</p>
              </div>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-stone-300 hover:bg-stone-800 rounded-lg transition-colors"
            data-testid="btn-logout"
          >
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>Déconnexion</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto w-full lg:w-auto">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white border-b border-stone-200 p-4 flex items-center justify-between sticky top-0 z-30">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-stone-100 rounded-lg"
          >
            <Menu className="h-6 w-6 text-stone-800" />
          </button>
          <div className="flex items-center gap-2">
            <span className="font-display text-xl text-stone-800">Kolnoa</span>
            <span className="text-xs text-stone-400">ERP</span>
          </div>
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
        
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/calendar" element={<CalendarView />} />
          <Route path="/clients" element={<ClientsList />} />
          <Route path="/clients/:id" element={<ClientDetails />} />
          <Route path="/devis" element={<DevisList />} />
          <Route path="/devis/new" element={<DevisEditor />} />
          <Route path="/devis/:id/edit" element={<DevisEditor />} />
          <Route path="/contrats" element={<ContratsList />} />
          <Route path="/notes-auteur" element={<NotesAuteurList />} />
          <Route path="/notes-auteur/new" element={<NoteAuteurEditor />} />
          <Route path="/notes-auteur/:id/edit" element={<NoteAuteurEditor />} />
          <Route path="/finances" element={<EvenementsList />} />
          <Route path="/finances/:id" element={<EvenementDetail />} />
          <Route path="/roadmaps" element={<RoadmapsList />} />
          <Route path="/roadmaps/new" element={<RoadmapCreator />} />
          <Route path="/roadmaps/:id/edit" element={<RoadmapEditor />} />
          <Route path="/prestations" element={<PrestationsList />} />
          <Route path="/categories" element={<CategoriesManagement />} />
          <Route path="/prestataires" element={<PrestataireUsers />} />
          <Route path="/roles" element={<RolesList />} />
          <Route path="/company" element={<CompanySettings />} />
          <Route path="/profile" element={<UserProfile />} />
          <Route path="/site" element={<SiteManager />} />
        </Routes>
      </main>
    </div>
  );
};

export default BackOffice;
